import type {ComponentType} from 'react';
import {genericSkinBase} from './genericSkinBase.ts';
import {BrushedMetal, Contours, Guilloche, Orbs, Rings} from './Ornaments.tsx';
import {ChipTone, type BankCardSkin} from './types.ts';

function paletteFace(gradient: string, glow: string, Ornament: ComponentType): ComponentType {
    const background = [
        `radial-gradient(70% 95% at 96% -8%, ${glow} 0%, transparent 70%)`,
        `radial-gradient(55% 80% at 4% 112%, ${glow} 0%, transparent 70%)`,
        gradient,
    ].join(', ');

    return function Face() {
        return (
            <>
                <div className="absolute inset-0" style={{background}} />
                <Ornament />
            </>
        );
    };
}

const DARK_TEXT = {
    contentColor: '#FFFFFF',
    labelColor: 'rgba(255, 255, 255, 0.72)',
    contactlessColor: 'rgba(255, 255, 255, 0.8)',
    mastercardWordColor: 'rgba(255, 255, 255, 0.85)',
};

const NAVY: BankCardSkin = {
    ...genericSkinBase,
    ...DARK_TEXT,
    Face: paletteFace('linear-gradient(155deg,#26356E 0%,#18224A 55%,#0E1533 100%)', 'rgba(79,107,216,0.32)', () => <Guilloche color="#9DB4FF" opacity={0.22} />),
};

const EMERALD: BankCardSkin = {
    ...genericSkinBase,
    ...DARK_TEXT,
    Face: paletteFace('linear-gradient(155deg,#137A60 0%,#0B5442 55%,#06362B 100%)', 'rgba(63,208,160,0.22)', () => <Rings color="#8FF0CC" opacity={0.16} />),
};

const BURGUNDY: BankCardSkin = {
    ...genericSkinBase,
    ...DARK_TEXT,
    Face: paletteFace('linear-gradient(155deg,#8A2438 0%,#621A29 55%,#3D0F1A 100%)', 'rgba(255,122,142,0.22)', () => <Orbs color="#FFFFFF" />),
};

const GRAPHITE: BankCardSkin = {
    ...genericSkinBase,
    ...DARK_TEXT,
    chipTone: ChipTone.Silver,
    Face: paletteFace('linear-gradient(155deg,#4A4F5C 0%,#2E323C 55%,#1B1E25 100%)', 'rgba(255,255,255,0.1)', BrushedMetal),
};

const CHAMPAGNE: BankCardSkin = {
    ...genericSkinBase,
    surfaceTone: 'light',
    contentColor: '#2B2414',
    labelColor: '#7A6A45',
    contactlessColor: '#8C7B55',
    mastercardWordColor: '#5C4E2E',
    Face: paletteFace('linear-gradient(155deg,#F4EAD3 0%,#E6D5AF 55%,#D3BD8C 100%)', 'rgba(255,255,255,0.45)', () => <Contours color="#9A7B3C" opacity={0.28} />),
};

const PALETTE: readonly BankCardSkin[] = [NAVY, EMERALD, BURGUNDY, GRAPHITE, CHAMPAGNE];

export function paletteSkinFor(seed: string): BankCardSkin {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    return PALETTE[Math.abs(hash) % PALETTE.length];
}
